import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { apiRequest, WIZARD_STEPS as steps } from "../utils/api";

const currentStep = 2;

const formatBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function GenerateManifest() {
  const navigate = useNavigate();
  const location = useLocation();

  const artifactId = location.state?.artifactId;

  const [manifest, setManifest] = useState(null);
  const [manifestSha256, setManifestSha256] = useState("");
  const [alreadyGenerated, setAlreadyGenerated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Safe to call again: the server returns the stored manifest unchanged
      // unless the artifact is still unsigned and new metadata is supplied.
      const data = await apiRequest("/api/artifact/generate-manifest", {
        method: "POST",
        json: { artifact_id: artifactId },
      });
      if (!data.manifest || !data.manifest_sha256) {
        throw new Error("The server returned an incomplete manifest. Please try again.");
      }
      setManifest(data.manifest);
      setManifestSha256(data.manifest_sha256);
      setAlreadyGenerated(!!data.already_generated);
    } catch (err) {
      setManifest(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    if (artifactId == null) {
      setLoading(false);
      setError("No artifact selected. Go back and upload a file first.");
      return;
    }
    generate();
  }, [artifactId, generate]);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(manifest, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const summary = manifest
    ? [
        { label: "Manifest ID", value: manifest.manifest_id, mono: true },
        { label: "Manifest SHA-256", value: manifestSha256, mono: true },
        { label: "Artifact SHA-256", value: manifest.firmware.sha256, mono: true },
        { label: "Artifact SHA-512", value: manifest.firmware.sha512, mono: true },
        { label: "Artifact Size", value: formatBytes(manifest.firmware.file_size) },
        {
          label: "Generated On",
          value: manifest.created_at ? new Date(manifest.created_at).toLocaleString() : "",
        },
      ]
    : [];

  const stepper = (
    <div className="flex items-center justify-between relative mb-5" style={{ height: "72px" }}>
      {steps.map((step, index) => {
        const isDone = index < currentStep;
        const isCurrent = index === currentStep;

        let circleClass = "bg-gray-200 text-gray-600";
        let labelClass = "text-gray-500";
        if (isDone) {
          circleClass = "bg-green-500 text-white";
          labelClass = "text-green-600";
        } else if (isCurrent) {
          circleClass = "bg-blue-600 text-white";
          labelClass = "text-blue-600";
        }

        return (
        <div key={step} className="flex-1 flex flex-col items-center relative">
          {index !== steps.length - 1 && (
            <div className={`absolute top-5 left-1/2 w-full h-[2px] ${index < currentStep ? "bg-green-500" : "bg-gray-300"}`} />
          )}
          <div
            className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${circleClass}`}
            style={{ width: "40px", height: "40px" }}
          >
            {index < currentStep ? <Check size={14} /> : index + 1}
          </div>
          <p className={`mt-2 text-[14px] font-medium text-center ${labelClass}`}>
            {step}
          </p>
        </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {stepper}

      {loading && (
        <div className="flex justify-center items-center h-64 text-gray-500">
          Generating manifest…
        </div>
      )}

      {!loading && error && (
        <div className="max-w-2xl">
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
            <p className="text-sm font-semibold text-red-700 mb-1">Manifest could not be generated</p>
            <p className="text-sm text-red-600">{error}</p>
            <p className="text-xs text-red-500 mt-2">Signing is not available until a manifest exists.</p>
          </div>
          <div className="flex justify-between">
            <button
              onClick={() => navigate("/upload-artifact")}
              className="text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition rounded-[10px]"
              style={{ height: "44px", padding: "0 24px" }}
            >
              Back
            </button>
            {artifactId != null && (
              <button
                onClick={generate}
                className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition rounded-[10px]"
                style={{ height: "44px", padding: "0 24px" }}
              >
                Try again
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && manifest && (
        <>
          <div className="flex gap-4 mb-4" style={{ alignItems: "flex-start" }}>

            {/* LEFT — MANIFEST PREVIEW */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-700" style={{ fontSize: "17px" }}>
                  Manifest Preview
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 transition"
                  >
                    {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  <button
                    onClick={() => setCollapsed((v) => !v)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 transition"
                  >
                    {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    {collapsed ? "Expand" : "Collapse"}
                  </button>
                </div>
              </div>

              {!collapsed && (
                <div className="rounded-2xl p-4 overflow-x-auto" style={{ background: "#0F172A" }}>
                  <pre style={{ fontFamily: "monospace", fontSize: "12px", lineHeight: "1.8", margin: 0, color: "#E2E8F0" }}>
                    <code>{JSON.stringify(manifest, null, 2)}</code>
                  </pre>
                </div>
              )}
            </div>

            {/* RIGHT — SUMMARY */}
            <div style={{ width: "300px", flexShrink: 0 }}>
              <h3 className="font-bold text-gray-700 mb-2" style={{ fontSize: "17px" }}>
                Manifest Summary
              </h3>
              <div className="border border-gray-200 rounded-2xl p-4 shadow-sm bg-white">
                <div className="divide-y divide-gray-100">
                  {summary.map((item) => (
                    <div key={item.label} className="py-2.5">
                      <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                      <p
                        className="text-xs font-medium text-gray-700 break-all"
                        style={item.mono ? { fontFamily: "monospace" } : {}}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check size={12} className="text-white" />
            </div>
            <p className="text-sm text-green-700 font-medium">
              {alreadyGenerated ? "Manifest already exists for this artifact. " : "Manifest created and stored. "}
              The signature will cover both the artifact digest and the manifest digest above,
              so changing either one afterwards makes verification fail.
            </p>
          </div>

          <div className="flex justify-between mt-2">
            <button
              onClick={() => navigate("/upload-artifact")}
              className="text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition rounded-[10px]"
              style={{ height: "44px", padding: "0 24px" }}
            >
              Back
            </button>
            <button
              onClick={() =>
                navigate("/signing-mode", {
                  state: { artifactId, manifest, manifestSha256 },
                })
              }
              className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition rounded-[10px]"
              style={{ height: "44px", padding: "0 24px" }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}